import {
	Alert,
	AlertDescription,
	AlertTitle,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	Input,
	Separator,
	Spinner,
} from "@monbits/ui";
import { AxiosError } from "axios";
import {
	Check,
	Eye,
	EyeOff,
	Key,
	Shield,
	ShieldAlert,
	ShieldCheck,
} from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { TwoFactorBackupCodes } from "src/components/two-factor/two-factor-backup-codes";
import { TwoFactorSetup } from "src/components/two-factor/two-factor-setup";
import { TwoFactorVerification } from "src/components/two-factor/two-factor-verification";
import { useTwoFactor } from "src/hooks/use-two-factor";
import type { TwoFactorSetupResponse } from "src/providers/auth-provider";
import authProvider from "src/providers/auth-provider";
import { validatePassword as validatePasswordStrength } from "src/utils/password-validator";

type Step = "idle" | "setup" | "verify-disable" | "backup-codes";

const AccountSettingsPage: React.FC = () => {
	// Two-Factor Authentication state
	const {
		status,
		isLoading: is2FALoading,
		isSubmitting: is2FASubmitting,
		fetchStatus,
		setupTwoFactor,
		enableTwoFactor,
		disableTwoFactor,
	} = useTwoFactor();

	const [step, setStep] = React.useState<Step>("idle");
	const [setupData, setSetupData] =
		React.useState<TwoFactorSetupResponse | null>(null);
	const [backupCodes, setBackupCodes] = React.useState<string[]>([]);

	// Password change state
	const [isChangePasswordOpen, setIsChangePasswordOpen] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [currentPassword, setCurrentPassword] = React.useState("");
	const [newPassword, setNewPassword] = React.useState("");
	const [confirmPassword, setConfirmPassword] = React.useState("");
	const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
	const [showNewPassword, setShowNewPassword] = React.useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
	const [currentPasswordError, setCurrentPasswordError] = React.useState("");
	const [newPasswordError, setNewPasswordError] = React.useState("");
	const [confirmPasswordError, setConfirmPasswordError] = React.useState("");

	React.useEffect(() => {
		fetchStatus();
	}, [fetchStatus]);

	// Password functions
	const resetPasswordForm = () => {
		setCurrentPassword("");
		setNewPassword("");
		setConfirmPassword("");
		setCurrentPasswordError("");
		setNewPasswordError("");
		setConfirmPasswordError("");
		setShowCurrentPassword(false);
		setShowNewPassword(false);
		setShowConfirmPassword(false);
	};

	const validateCurrentPassword = (value: string): boolean => {
		if (!value) {
			setCurrentPasswordError("Please enter your current password");
			return false;
		}
		setCurrentPasswordError("");
		return true;
	};

	const validateNewPassword = (value: string): boolean => {
		if (!value) {
			setNewPasswordError("Please enter a new password");
			return false;
		}
		if (value === currentPassword) {
			setNewPasswordError(
				"New password must be different from current password",
			);
			return false;
		}

		const result = validatePasswordStrength(value);
		if (!result.valid) {
			setNewPasswordError(result.errors[0]); // Show first error
			return false;
		}

		setNewPasswordError("");
		return true;
	};

	const validateConfirmPassword = (value: string): boolean => {
		if (!value) {
			setConfirmPasswordError("Please confirm your new password");
			return false;
		}
		if (value !== newPassword) {
			setConfirmPasswordError("Passwords do not match");
			return false;
		}
		setConfirmPasswordError("");
		return true;
	};

	const handleChangePassword = async (e: React.FormEvent) => {
		e.preventDefault();

		const isCurrentValid = validateCurrentPassword(currentPassword);
		const isNewValid = validateNewPassword(newPassword);
		const isConfirmValid = validateConfirmPassword(confirmPassword);

		if (!isCurrentValid || !isNewValid || !isConfirmValid) {
			return;
		}

		try {
			setIsSubmitting(true);
			await authProvider.changePassword(currentPassword, newPassword);

			toast.success("Password changed successfully!", {
				description: "Your password has been updated",
				icon: <Check className="h-4 w-4" />,
			});

			setIsChangePasswordOpen(false);
			resetPasswordForm();
		} catch (error) {
			console.error("Change password error:", error);
			if (error instanceof AxiosError) {
				const message =
					error.response?.data?.message || "Failed to change password";
				if (
					message.includes("current password") ||
					message.includes("invalid")
				) {
					setCurrentPasswordError("Current password is incorrect");
				} else {
					toast.error("Error", { description: message });
				}
			} else {
				toast.error("Error", {
					description: "Failed to change password. Please try again.",
				});
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	// Two-Factor functions
	const handleEnableClick = async () => {
		const data = await setupTwoFactor();
		if (data) {
			setSetupData(data);
			setStep("setup");
		}
	};

	const handleSetupVerify = async (code: string) => {
		if (!setupData) return false;
		const success = await enableTwoFactor(
			code,
			setupData.secret,
			setupData.backupCodes,
		);
		if (success) {
			setBackupCodes(setupData.backupCodes);
			setStep("backup-codes");
		}
		return success;
	};

	const handleDisableClick = () => {
		setStep("verify-disable");
	};

	const handleDisableVerify = async (code: string) => {
		const success = await disableTwoFactor(code);
		if (success) {
			setStep("idle");
		}
		return success;
	};

	const handleCloseDialog = () => {
		setStep("idle");
		setSetupData(null);
		setBackupCodes([]);
	};

	const is2FAEnabled = status?.enabled ?? false;

	return (
		<div className="p-4 sm:p-6">
			<div className="mb-8">
				<h1 className="text-3xl font-bold">Account Settings</h1>
				<p className="text-sm text-muted-foreground mt-1">
					Manage your account security and preferences
				</p>
			</div>

			<div className="max-w-3xl">
				<Card>
					<CardHeader>
						<div className="flex items-center gap-2">
							<Shield className="h-5 w-5" />
							<CardTitle>Security</CardTitle>
						</div>
						<CardDescription>
							Protect your account with a strong password and two-factor
							authentication
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-6">
						{/* Password Section */}
						<div className="space-y-4">
							<div className="flex items-start justify-between">
								<div className="flex items-start gap-3">
									<div className="rounded-lg bg-muted p-2">
										<Key className="h-4 w-4" />
									</div>
									<div>
										<h4 className="font-medium">Password</h4>
										<p className="text-sm text-muted-foreground mt-1">
											Change your password to keep your account secure
										</p>
										<p className="text-xs text-muted-foreground mt-2">
											Requires: 12+ characters, uppercase, lowercase, digit, and
											special character
										</p>
									</div>
								</div>
								<Button
									variant="outline"
									onClick={() => setIsChangePasswordOpen(true)}
								>
									Change
								</Button>
							</div>
						</div>

						<Separator />

						{/* Two-Factor Authentication Section */}
						<div className="space-y-4">
							<div className="flex items-start justify-between">
								<div className="flex items-start gap-3">
									<div
										className={`rounded-lg p-2 ${is2FAEnabled ? "bg-green-100 dark:bg-green-900/20" : "bg-muted"}`}
									>
										{is2FAEnabled ? (
											<ShieldCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
										) : (
											<ShieldAlert className="h-4 w-4" />
										)}
									</div>
									<div className="flex-1">
										<div className="flex items-center gap-2">
											<h4 className="font-medium">Two-Factor Authentication</h4>
											{is2FAEnabled && (
												<span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
													Enabled
												</span>
											)}
										</div>
										<p className="text-sm text-muted-foreground mt-1">
											{is2FAEnabled
												? "Your account is protected with an authenticator app"
												: "Add an extra layer of security with a verification code from your phone"}
										</p>
									</div>
								</div>
								<Button
									variant={is2FAEnabled ? "outline" : "default"}
									onClick={
										is2FAEnabled ? handleDisableClick : handleEnableClick
									}
									disabled={is2FALoading || is2FASubmitting}
								>
									{is2FAEnabled ? "Disable" : "Enable"}
								</Button>
							</div>

							{!is2FAEnabled && (
								<Alert>
									<ShieldAlert className="h-4 w-4" />
									<AlertTitle>Recommended</AlertTitle>
									<AlertDescription>
										Enable 2FA to protect your account from unauthorized access,
										even if your password is compromised.
									</AlertDescription>
								</Alert>
							)}
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Password Change Dialog */}
			<Dialog
				open={isChangePasswordOpen}
				onOpenChange={setIsChangePasswordOpen}
			>
				<DialogContent>
					<form onSubmit={handleChangePassword}>
						<DialogHeader>
							<DialogTitle>Change Password</DialogTitle>
							<DialogDescription>
								Enter your current password and choose a new secure password
							</DialogDescription>
						</DialogHeader>

						<FieldGroup className="py-4">
							<Field>
								<FieldLabel>Current Password</FieldLabel>
								<div className="relative">
									<Input
										type={showCurrentPassword ? "text" : "password"}
										placeholder="Enter current password"
										value={currentPassword}
										onChange={(e) => {
											setCurrentPassword(e.target.value);
											if (currentPasswordError)
												validateCurrentPassword(e.target.value);
										}}
										className={
											currentPasswordError
												? "border-destructive pr-10"
												: "pr-10"
										}
									/>
									<button
										type="button"
										onClick={() => setShowCurrentPassword(!showCurrentPassword)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
									>
										{showCurrentPassword ? (
											<EyeOff className="h-4 w-4" />
										) : (
											<Eye className="h-4 w-4" />
										)}
									</button>
								</div>
								{currentPasswordError && (
									<FieldDescription className="text-destructive">
										{currentPasswordError}
									</FieldDescription>
								)}
							</Field>

							<Field>
								<FieldLabel>New Password</FieldLabel>
								<div className="relative">
									<Input
										type={showNewPassword ? "text" : "password"}
										placeholder="Enter new password (min. 12 characters)"
										value={newPassword}
										onChange={(e) => {
											setNewPassword(e.target.value);
											if (newPasswordError) validateNewPassword(e.target.value);
											if (confirmPassword)
												validateConfirmPassword(confirmPassword);
										}}
										className={
											newPasswordError ? "border-destructive pr-10" : "pr-10"
										}
									/>
									<button
										type="button"
										onClick={() => setShowNewPassword(!showNewPassword)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
									>
										{showNewPassword ? (
											<EyeOff className="h-4 w-4" />
										) : (
											<Eye className="h-4 w-4" />
										)}
									</button>
								</div>
								<FieldDescription
									className={
										newPasswordError
											? "text-destructive"
											: "text-muted-foreground"
									}
								>
									{newPasswordError ||
										"Must include uppercase, lowercase, digit, and special character"}
								</FieldDescription>
							</Field>

							<Field>
								<FieldLabel>Confirm New Password</FieldLabel>
								<div className="relative">
									<Input
										type={showConfirmPassword ? "text" : "password"}
										placeholder="Confirm new password"
										value={confirmPassword}
										onChange={(e) => {
											setConfirmPassword(e.target.value);
											if (confirmPasswordError)
												validateConfirmPassword(e.target.value);
										}}
										className={
											confirmPasswordError
												? "border-destructive pr-10"
												: "pr-10"
										}
									/>
									<button
										type="button"
										onClick={() => setShowConfirmPassword(!showConfirmPassword)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
									>
										{showConfirmPassword ? (
											<EyeOff className="h-4 w-4" />
										) : (
											<Eye className="h-4 w-4" />
										)}
									</button>
								</div>
								{confirmPasswordError && (
									<FieldDescription className="text-destructive">
										{confirmPasswordError}
									</FieldDescription>
								)}
							</Field>
						</FieldGroup>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => {
									setIsChangePasswordOpen(false);
									resetPasswordForm();
								}}
								disabled={isSubmitting}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting ? (
									<span className="flex items-center gap-2">
										<Spinner className="size-4" />
										Changing...
									</span>
								) : (
									"Change Password"
								)}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Two-Factor Authentication Dialogs */}
			<Dialog open={step !== "idle"} onOpenChange={handleCloseDialog}>
				<DialogContent className="max-w-md">
					{step === "setup" && setupData && (
						<>
							<DialogHeader>
								<DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
								<DialogDescription>
									Scan the QR code with your authenticator app
								</DialogDescription>
							</DialogHeader>
							<TwoFactorSetup
								otpURL={setupData.otpUrl}
								secret={setupData.secret}
								onVerify={handleSetupVerify}
								onCancel={handleCloseDialog}
								isSubmitting={is2FASubmitting}
							/>
						</>
					)}

					{step === "verify-disable" && (
						<>
							<DialogHeader>
								<DialogTitle>Disable Two-Factor Authentication</DialogTitle>
								<DialogDescription>
									Enter your verification code to disable 2FA
								</DialogDescription>
							</DialogHeader>
							<TwoFactorVerification
								onVerify={handleDisableVerify}
								onCancel={handleCloseDialog}
								isSubmitting={is2FASubmitting}
								showBackupOption={true}
								description="Enter the code from your authenticator app"
								submitLabel="Disable 2FA"
							/>
						</>
					)}

					{step === "backup-codes" && backupCodes.length > 0 && (
						<>
							<DialogHeader>
								<DialogTitle>Backup Codes</DialogTitle>
								<DialogDescription>
									Save these codes in a safe place
								</DialogDescription>
							</DialogHeader>
							<TwoFactorBackupCodes
								codes={backupCodes}
								onClose={handleCloseDialog}
								showWarning={true}
							/>
						</>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default AccountSettingsPage;
